/**
 * Permission codes the Divisions screens (FSD 1.2.2 "Divisions and Management
 * Hierarchy") declare an interest in.
 *
 * They are the dot-strings `server/internal/org/permissions.go` (`PermDivisionRead`/
 * `PermDivisionManage`, pre-existing from C1) and `server/internal/org/permissions_fnd.go`
 * (§SP8: `PermDivisionBalancingConfirm`, `PermCrossChargeRead`/`Manage`/`Approve`)
 * register with the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`.
 *
 * `read`/`manage` cover list/get/create/update/submit/activate/end-date/reorganise/
 * rollup/countries/legal-entities/segment-map — every division write is checked on
 * `manage`, so an Auditor holding only `read` can open the register and every
 * division and change nothing (1.2.2.10: "Divisional owners hold read access to
 * their own division and its rollup, and no write access to the division record
 * itself").
 *
 * `balancingConfirm` is FSD 1.2.2.4's "Map to Segment... selecting a balancing
 * segment raises a warning requiring Finance confirmation" (F-3.006, FND-F02-V04) —
 * the Finance Manager's own slice, distinct from the ordinary `manage` every other
 * division write uses (`segmentmap.go`'s own doc comment).
 *
 * Cross-charging is its own three-way split (1.2.2.10: "Cross-charging arrangements
 * require dual approval — divisional owner and Finance Controller — because they
 * generate financial entries"): `crossChargeManage` drafts a rule and runs its
 * simulation (the divisional owner's slice), `crossChargeApprove` activates it once
 * balanced (the Finance Manager's slice, distinct from `crossChargeManage` so the
 * hand that proposes a recovery is never the hand that puts it into effect).
 */

export const DIVISION_PERMISSIONS = {
  read: 'org.division.read',
  manage: 'org.division.manage',

  /** FSD 1.2.2.4/F-3.006/FND-F02-V04 — confirming a balancing-segment mapping. */
  balancingConfirm: 'org.division.balancing_confirm',

  crossChargeRead: 'org.cross_charge.read',
  crossChargeManage: 'org.cross_charge.manage',
  crossChargeApprove: 'org.cross_charge.approve',
} as const

export type DivisionPermission = (typeof DIVISION_PERMISSIONS)[keyof typeof DIVISION_PERMISSIONS]
